// RegistrationComplete component
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { post } from '../api/axiosClient';
import './RegistrationComplete.css';

const RegistrationComplete: React.FC = () => {
  const location = useLocation();
  const [email, setEmail] = useState<string>('');
  const [codeDigits, setCodeDigits] = useState<string[]>(['', '', '', '', '', '']);
  const inputsRef = useRef<HTMLInputElement[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState<boolean>(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const codeParam = params.get('code');
    if (codeParam && /^\d{6}$/.test(codeParam)) {
      setCodeDigits(codeParam.split(''));
    }
  }, [location.search]);

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...codeDigits];
    next[index] = value;
    setCodeDigits(next);
    if (value && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pasted)) {
      e.preventDefault();
      setCodeDigits(pasted.split(''));
      inputsRef.current[5]?.focus();
    }
  };

  const isValidEmail = (val: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const codeValue = codeDigits.join('');
  const canSubmit = isValidEmail(email) && /^\d{6}$/.test(codeValue) && !isSubmitting;

  const handleVerify = async () => {
    setError(null);
    setSuccess(null);
    if (!canSubmit) return;

    try {
      setIsSubmitting(true);
      await post<any>('/user/auth/verify-email', {
        email,
        code: codeValue,
      });
      setSuccess('Email verified successfully. You may continue to Sportagram.');
      setIsVerified(true);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Verification failed. Please check the code and try again.';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="registration-complete-container">
      <div className="success-card">
        {!isVerified && (
          <div className="pending-hero">
            <div className="pending-icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4 7.5C4 6.11929 5.11929 5 6.5 5H17.5C18.8807 5 20 6.11929 20 7.5V16.5C20 17.8807 18.8807 19 17.5 19H6.5C5.11929 19 4 17.8807 4 16.5V7.5Z"
                  stroke="currentColor"
                  strokeWidth="1.75"
                />
                <path
                  d="M5.5 7L10.8 11.1C11.5 11.64 12.5 11.64 13.2 11.1L18.5 7"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h1 className="pending-title">One last step before kickoff</h1>
            <p className="pending-subtitle">
              Enter the 6-digit code from your inbox to unlock your Sportagram account.
            </p>

            <div className="pending-tips" role="list">
              <div className="pending-tip" role="listitem">
                Check Spam/Junk if it’s not in your inbox
              </div>
              <div className="pending-tip" role="listitem">
                Codes are usually delivered within a minute
              </div>
              <div className="pending-tip" role="listitem">
                You can paste the full code into the first box
              </div>
            </div>
          </div>
        )}

        {!isVerified && (
          <div className="verify-section">
            <h2 className="verify-title">Verify your email</h2>
            <p className="verify-subtitle">Enter your email and 6-digit code to complete verification.</p>

            <div className="verify-form">
              <input
                type="email"
                className="verify-email-input"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                aria-label="Email address"
              />

              <div className="verify-code-inputs">
                {codeDigits.map((digit, idx) => (
                  <input
                    key={idx}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    className="verify-code-input"
                    value={digit}
                    onChange={(e) => handleCodeChange(idx, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(idx, e)}
                    onPaste={idx === 0 ? handlePaste : undefined}
                    autoComplete="one-time-code"
                    aria-label={`Verification code digit ${idx + 1}`}
                    ref={(el) => {
                      if (el) {
                        inputsRef.current[idx] = el;
                      }
                    }}
                  />
                ))}
              </div>

              <button
                className="primary-button"
                onClick={handleVerify}
                disabled={!canSubmit}
              >
                {isSubmitting ? 'Verifying…' : 'Verify Email'}
              </button>

              {error && <div className="error-text">{error}</div>}
              {success && <div className="success-text">{success}</div>}
            </div>
          </div>
        )}

        {isVerified && (
          <>
            <div className="success-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#10B981" />
                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <h1 className="success-title">Congratulations!</h1>
            <h2 className="success-subtitle">Your Registration is Now Complete</h2>

            <div className="welcome-message">
              <p className="welcome-text">
                Welcome to <span className="brand-name">Sportagram</span> – where passion meets performance!
              </p>
              <p className="description-text">
                You've just joined a community of athletes, coaches, and sports enthusiasts who are
                dedicated to pushing boundaries and achieving greatness. Get ready to track your progress,
                connect with like-minded individuals, and take your athletic journey to the next level.
              </p>
            </div>
          </>
        )}

        {/* <div className="next-steps">
          <h3>What's Next?</h3>
          <ul className="steps-list">
            <li>
              <span className="step-icon">📱</span>
              Download the Sportagram mobile app
            </li>
            <li>
              <span className="step-icon">👤</span>
              Complete your profile
            </li>
            <li>
              <span className="step-icon">🏆</span>
              Start tracking your workouts and achievements
            </li>
            <li>
              <span className="step-icon">🤝</span>
              Connect with coaches and fellow athletes
            </li>
          </ul>
        </div>

        <div className="action-buttons">
          <button className="primary-button">
            Get Started Now
          </button>
          <button className="secondary-button">
            Download Mobile App
          </button>
        </div>

        <div className="social-proof">
          <p className="social-text">
            Join over <strong>50,000+</strong> athletes already using Sportagram
          </p>
          <div className="rating">
            <span className="stars">⭐⭐⭐⭐⭐</span>
            <span className="rating-text">4.9/5 App Store Rating</span>
          </div>
        </div> */}
      </div>
      
      <div className="background-decoration">
        <div className="decoration-circle circle-1"></div>
        <div className="decoration-circle circle-2"></div>
        <div className="decoration-circle circle-3"></div>
      </div>
    </div>
  );
}

export default RegistrationComplete;
